import { useState } from "react";
import { bbbLeaderboard, initials, daySkins, tripSkinsLeaderboard, dayNassau, tripNassauLeaderboard } from "@/lib/scoring";
import { Coins, Circle, ChevronDown, ChevronUp, Swords, Wallet, Copy } from "lucide-react";
import { toast } from "sonner";

export default function BbbTab({ event, currentDay }) {
  const [scope, setScope] = useState("event"); // "event" | "day"
  const [expanded, setExpanded] = useState(true);
  const cur = event.currency || "$";
  const bbbRows = bbbLeaderboard(event, scope === "day" ? currentDay?.id : null);
  const skinsRes = currentDay ? daySkins(currentDay, event) : null;
  const tripSkins = tripSkinsLeaderboard(event);
  const nassauRes = currentDay ? dayNassau(currentDay, event) : null;
  const tripNassau = tripNassauLeaderboard(event);
  const skinsScope = scope === "day" ? "day" : "event";

  // Settle-up = combined Skins + Nassau winnings per player, event-wide
  const settleMap = {};
  for (const p of event.players) settleMap[p.id] = { player: p, skinsMoney: 0, nassauMoney: 0, skins: 0, segments: 0 };
  for (const r of tripSkins) if (settleMap[r.player.id]) { settleMap[r.player.id].skinsMoney = r.money; settleMap[r.player.id].skins = r.skins; }
  for (const r of tripNassau) if (settleMap[r.player.id]) { settleMap[r.player.id].nassauMoney = r.money; settleMap[r.player.id].segments = r.segmentsWon; }
  const settleRows = Object.values(settleMap)
    .map((r) => ({ ...r, total: (r.skinsMoney || 0) + (r.nassauMoney || 0) }))
    .sort((a, b) => b.total - a.total);
  const settleTotal = settleRows.reduce((a, r) => a + r.total, 0);

  const copySettle = async () => {
    const lines = [
      `${event.name} — Settle up (${cur})`,
      ...settleRows
        .filter((r) => r.total > 0)
        .map((r) => `${r.player.name}: ${cur}${r.total.toFixed(0)}  (Skins ${cur}${r.skinsMoney.toFixed(0)} · Nassau ${cur}${r.nassauMoney.toFixed(0)})`),
      settleTotal > 0 ? `Pot: ${cur}${settleTotal.toFixed(0)}` : "",
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("Settle-up copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-4 tga-fade">
      <div className="tga-card-dark">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl text-[color:var(--brass-bright)]">Side games</div>
          <div className="pill-toggle" style={{ borderColor: "var(--border-brass)" }}>
            <button data-testid="bbb-scope-event" className={scope === "event" ? "on" : ""} onClick={() => setScope("event")} style={{ color: scope === "event" ? "#14251b" : "var(--text-cream)" }}>Trip</button>
            <button data-testid="bbb-scope-day" className={scope === "day" ? "on" : ""} onClick={() => setScope("day")} style={{ color: scope === "day" ? "#14251b" : "var(--text-cream)" }}>{currentDay?.label || "Day"}</button>
          </div>
        </div>
        <p className="helptext mt-1">Live standings for BBB and Skins across the trip or the selected day.</p>
      </div>

      {/* Settle up */}
      {settleTotal > 0 && (
        <div className="tga-card">
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={16} className="text-[color:var(--brass-dark)]" />
            <div className="font-display text-xl">Settle up</div>
            <span className="ml-1 text-[10px] uppercase tracking-wide font-mono-metrics bg-[rgba(197,160,89,0.15)] text-[color:var(--brass-dark)] rounded-full px-2 py-0.5 border border-[rgba(197,160,89,0.4)]">
              pot {cur}{settleTotal.toFixed(0)}
            </span>
            <button data-testid="settle-copy-btn" onClick={copySettle} className="ml-auto ghost-btn rounded-md p-1.5 text-xs inline-flex items-center gap-1" title="Copy summary">
              <Copy size={12} /> Copy
            </button>
          </div>
          <table className="w-full text-sm font-mono-metrics">
            <thead>
              <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                <th className="py-1">Player</th>
                <th className="py-1 text-right">Skins</th>
                <th className="py-1 text-right">Nassau</th>
                <th className="py-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {settleRows.filter((r) => r.total > 0 || r.player).map((r) => (
                <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)]" data-testid={`settle-row-${r.player.id}`}>
                  <td className="py-2 flex items-center gap-2">
                    <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                    <span className="font-sans">{r.player.name}</span>
                  </td>
                  <td className="py-2 text-right">{r.skinsMoney > 0 ? `${cur}${r.skinsMoney.toFixed(0)}` : "—"}</td>
                  <td className="py-2 text-right">{r.nassauMoney > 0 ? `${cur}${r.nassauMoney.toFixed(0)}` : "—"}</td>
                  <td className={`py-2 text-right font-bold ${r.total > 0 ? "text-[color:var(--brass-dark)]" : ""}`}>{r.total > 0 ? `${cur}${r.total.toFixed(0)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="helptext text-[color:var(--text-dark)]/60 mt-2 text-xs">Cash winnings across every day. Tap Copy to share the summary in your group chat.</p>
        </div>
      )}

      {/* Skins */}
      <div className="tga-card">
        <div className="flex items-center gap-2 mb-3">
          <Coins size={16} className="text-[color:var(--brass-dark)]" />
          <div className="font-display text-xl">Skins</div>
          {currentDay && currentDay.skinsMode && currentDay.skinsMode !== "off" && (
            <span className="ml-1 text-[10px] uppercase tracking-wide font-mono-metrics bg-[rgba(197,160,89,0.15)] text-[color:var(--brass-dark)] rounded-full px-2 py-0.5 border border-[rgba(197,160,89,0.4)]">
              {currentDay.skinsMode}
            </span>
          )}
        </div>

        {skinsScope === "event" ? (
          tripSkins.every((r) => r.skins === 0) ? (
            <p className="helptext text-[color:var(--text-dark)]/70">Enable Skins on a day (Setup) and record scores to see standings.</p>
          ) : (
            <table className="w-full text-sm font-mono-metrics">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1">#</th>
                  <th className="py-1">Player</th>
                  <th className="py-1 text-right">Days</th>
                  <th className="py-1 text-right">Skins</th>
                  <th className="py-1 text-right">$</th>
                </tr>
              </thead>
              <tbody>
                {tripSkins.map((r, i) => (
                  <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)]">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2 flex items-center gap-2">
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                      <span className="font-sans">{r.player.name}</span>
                    </td>
                    <td className="py-2 text-right">{r.days}</td>
                    <td className="py-2 text-right font-bold">{r.skins}</td>
                    <td className="py-2 text-right font-bold text-[color:var(--brass-dark)]">{r.money > 0 ? `${cur}${r.money.toFixed(0)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : !skinsRes ? (
          <p className="helptext text-[color:var(--text-dark)]/70">Skins not enabled on {currentDay?.label || "this day"}. Turn it on in Setup (Gross or Net).</p>
        ) : (
          <>
            {skinsRes.value > 0 && (
              <div className="text-[11px] font-mono-metrics text-[color:var(--brass-dark)] mb-2">
                {cur}{skinsRes.value.toFixed(0)} per skin
              </div>
            )}
            <table className="w-full text-sm font-mono-metrics mb-3">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1">#</th>
                  <th className="py-1">Player</th>
                  <th className="py-1 text-right">Skins</th>
                  {skinsRes.value > 0 && <th className="py-1 text-right">$</th>}
                </tr>
              </thead>
              <tbody>
                {skinsRes.rows.map((r, i) => (
                  <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)]">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2 flex items-center gap-2">
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                      <span className="font-sans">{r.player.name}</span>
                    </td>
                    <td className="py-2 text-right font-bold">{r.skins}</td>
                    {skinsRes.value > 0 && (
                      <td className="py-2 text-right font-bold text-[color:var(--brass-dark)]">{r.money > 0 ? `${cur}${r.money.toFixed(0)}` : "—"}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            <button
              className="w-full flex items-center justify-between text-xs text-[color:var(--brass-dark)] uppercase tracking-wide py-2 border-t border-[rgba(27,51,34,0.1)]"
              onClick={() => setExpanded((e) => !e)}
              data-testid="skins-holes-toggle"
            >
              <span>Hole-by-hole</span>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {expanded && (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 mt-2 font-mono-metrics text-xs">
                {skinsRes.holes.map((h) => {
                  const winner = h.winnerId ? event.players.find((p) => p.id === h.winnerId) : null;
                  const bgCls = h.tied
                    ? "border border-dashed border-[rgba(197,160,89,0.5)] bg-[rgba(197,160,89,0.05)]"
                    : winner
                    ? "border border-[color:var(--brass-dark)] bg-[rgba(197,160,89,0.18)]"
                    : "border border-[rgba(27,51,34,0.1)] bg-[rgba(27,51,34,0.02)]";
                  return (
                    <div key={h.holeIdx} className={`rounded-md p-1 text-center ${bgCls}`} data-testid={`skins-hole-${h.holeIdx}`}>
                      <div className="text-[10px] opacity-70">H{h.holeIdx + 1}</div>
                      {!h.decided ? (
                        <div className="text-[10px] text-[color:var(--brass-dark)] italic">—</div>
                      ) : h.tied ? (
                        <div className="text-[10px] text-[color:var(--brass-dark)]">TIE<br/><span className="opacity-70">carry {h.carry + 1}</span></div>
                      ) : (
                        <div className="text-[11px]">
                          <div className="font-bold truncate" title={winner?.name}>{winner ? initials(winner.name) : "—"}</div>
                          <div className="text-[10px] text-[color:var(--brass-dark)]">
                            +{h.value}{skinsRes.value > 0 && ` · ${cur}${(h.value * skinsRes.value).toFixed(0)}`}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {skinsRes.unresolvedCarry > 0 && (
              <div className="mt-2 text-[11px] text-[color:var(--brass-dark)] font-mono-metrics text-center">
                {skinsRes.unresolvedCarry} skin{skinsRes.unresolvedCarry === 1 ? "" : "s"} still carried after 18 holes
              </div>
            )}
          </>
        )}
      </div>

      {/* Nassau */}
      <div className="tga-card">
        <div className="flex items-center gap-2 mb-3">
          <Swords size={16} className="text-[color:var(--brass-dark)]" />
          <div className="font-display text-xl">Nassau</div>
          {currentDay && currentDay.nassauMode && currentDay.nassauMode !== "off" && (
            <span className="ml-1 text-[10px] uppercase tracking-wide font-mono-metrics bg-[rgba(197,160,89,0.15)] text-[color:var(--brass-dark)] rounded-full px-2 py-0.5 border border-[rgba(197,160,89,0.4)]">
              {currentDay.nassauMode}
            </span>
          )}
        </div>

        {skinsScope === "event" ? (
          tripNassau.every((r) => r.segmentsWon === 0) ? (
            <p className="helptext text-[color:var(--text-dark)]/70">Enable Nassau on a day (Setup) and record scores to see standings.</p>
          ) : (
            <table className="w-full text-sm font-mono-metrics">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1">#</th>
                  <th className="py-1">Player</th>
                  <th className="py-1 text-right">Days</th>
                  <th className="py-1 text-right">Segs</th>
                  <th className="py-1 text-right">{cur}</th>
                </tr>
              </thead>
              <tbody>
                {tripNassau.map((r, i) => (
                  <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)]">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2 flex items-center gap-2">
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                      <span className="font-sans">{r.player.name}</span>
                    </td>
                    <td className="py-2 text-right">{r.days}</td>
                    <td className="py-2 text-right font-bold">{r.segmentsWon.toFixed(r.segmentsWon % 1 ? 1 : 0)}</td>
                    <td className="py-2 text-right font-bold text-[color:var(--brass-dark)]">{r.money > 0 ? `${cur}${r.money.toFixed(0)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : !nassauRes ? (
          <p className="helptext text-[color:var(--text-dark)]/70">Nassau not enabled on {currentDay?.label || "this day"}. Turn it on in Setup (Gross or Net).</p>
        ) : (
          <>
            {nassauRes.value > 0 && (
              <div className="text-[11px] font-mono-metrics text-[color:var(--brass-dark)] mb-2">
                {cur}{nassauRes.value.toFixed(0)} per segment · 3 segments per day
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {["front", "back", "overall"].map((k) => {
                const seg = nassauRes.segments[k];
                const label = k === "front" ? "Front 9" : k === "back" ? "Back 9" : "Overall";
                let display;
                if (!seg.complete) display = <span className="italic opacity-60">In play</span>;
                else if (seg.winners.length === 0) display = "—";
                else if (seg.winners.length === 1) {
                  const w = event.players.find((p) => p.id === seg.winners[0]);
                  display = <span className="font-bold">{w?.name || "?"}</span>;
                } else {
                  display = <span className="text-[color:var(--brass-dark)]">Split · {seg.winners.length}</span>;
                }
                return (
                  <div key={k} className="text-center border border-[rgba(27,51,34,0.15)] rounded-lg p-2" data-testid={`nassau-seg-${k}`}>
                    <div className="text-[10px] uppercase text-[color:var(--brass-dark)] tracking-wide">{label}</div>
                    <div className="text-sm mt-1">{display}</div>
                    {nassauRes.value > 0 && seg.complete && seg.winners.length > 0 && (
                      <div className="text-[10px] text-[color:var(--brass-dark)] font-mono-metrics mt-0.5">{cur}{(nassauRes.value / seg.winners.length).toFixed(0)} ea</div>
                    )}
                  </div>
                );
              })}
            </div>
            <table className="w-full text-sm font-mono-metrics">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1">#</th>
                  <th className="py-1">Player</th>
                  <th className="py-1 text-right">Segs</th>
                  {nassauRes.value > 0 && <th className="py-1 text-right">{cur}</th>}
                </tr>
              </thead>
              <tbody>
                {nassauRes.rows.map((r, i) => (
                  <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)]">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2 flex items-center gap-2">
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                      <span className="font-sans">{r.player.name}</span>
                    </td>
                    <td className="py-2 text-right font-bold">{r.segmentsWon.toFixed(r.segmentsWon % 1 ? 1 : 0)}</td>
                    {nassauRes.value > 0 && (
                      <td className="py-2 text-right font-bold text-[color:var(--brass-dark)]">{r.money > 0 ? `${cur}${r.money.toFixed(0)}` : "—"}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Bingo Bango Bongo */}
      <div className="tga-card">
        <div className="flex items-center gap-2 mb-3">
          <Circle size={16} className="text-[color:var(--brass-dark)]" />
          <div className="font-display text-xl">Bingo · Bango · Bongo</div>
        </div>
        {bbbRows.length === 0 ? (
          <p className="helptext text-[color:var(--text-dark)]/70">No players yet.</p>
        ) : (
          <table className="w-full text-sm font-mono-metrics">
            <thead>
              <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                <th className="py-1">#</th>
                <th className="py-1">Player</th>
                <th className="py-1 text-right">Bin</th>
                <th className="py-1 text-right">Ban</th>
                <th className="py-1 text-right">Bon</th>
                <th className="py-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {bbbRows.map((r, i) => (
                <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)]">
                  <td className="py-2">{i + 1}</td>
                  <td className="py-2 flex items-center gap-2">
                    <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                    <span className="font-sans">{r.player.name}</span>
                  </td>
                  <td className="py-2 text-right">{r.bingo}</td>
                  <td className="py-2 text-right">{r.bango}</td>
                  <td className="py-2 text-right">{r.bongo}</td>
                  <td className="py-2 text-right font-bold">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="helptext text-[color:var(--text-dark)]/60 mt-2">1 point each for Bingo (first on green), Bango (closest once all on), Bongo (first in cup). Log picks per hole in the Score tab.</p>
      </div>
    </div>
  );
}
